const exists = (value) => value !== null && value !== undefined;

const isEmpty = (value) => value === '' || value === undefined || value === null;

const isEmptyTrimmed = (value) => {
	if (typeof value === 'string') {
		return value.trim() === '';
	}
	return true;
};

export const validations = {
	matchRegexp: (value, { regexp }) => {
		const validationRegexp = (regexp instanceof RegExp ? regexp : (new RegExp(regexp)));
		if (!exists(value) || isEmpty(value)) {
			return false;
		}
		return validationRegexp.test(value);
	},

	callback: (value, callback) => {
		if (callback instanceof Function) {
			return callback(value);
		}
		throw new Error('Callback validator expects fn to be instance of Function.');
	},

	// eslint-disable-next-line
	isEmail: value => validations.matchRegexp(value, { regexp: /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i }),

	isEmpty: value => isEmpty(value),

	notBlank: value => !isEmpty(value),

	trim: value => !isEmptyTrimmed(value),

	isNumber: value => validations.matchRegexp(value, /^-?[0-9]\d*(\d+)?$/i),

	isFloat: value => validations.matchRegexp(value, /^(?:[1-9]\d*|0)?(?:\.\d+)?$/i),

	isPositive: (value) => {
		if (exists(value)) {
			return (validations.isNumber(value) || validations.isFloat(value)) && value >= 0;
		}
		return true;
	},

	maxNumber: (value, { max }) => !exists(value) || isEmpty(value) || parseInt(value, 10) <= parseInt(max, 10),

	minNumber: (value, { min }) => !exists(value) || isEmpty(value) || parseInt(value, 10) >= parseInt(min, 10),

	isString: value => !isEmpty(value) || typeof value === 'string' || value instanceof String,
	minStringLength: (value, { length }) => validations.isString(value) && value.length >= length,
	maxStringLength: (value, { length }) => validations.isString(value) && value.length <= length,
	minMaxStringLength: (value, { min, max }) => validations.isString(value) && value.length >= min && value.length <= max
};

export const validationMessages = {
	matchRegexp: ({ regexp }) => `Value must match regex ${regexp}`,
	isEmail: () => `Value must be a valid email.`,
	notBlank: () => `Value cannot be empty.`,
	minStringLength: ({ length }) => `Value must be at least ${length} characters long.`,
	maxStringLength: ({ length }) => `Value cannot be longer than ${length} characters.`,
	minMaxStringLength: ({ min, max }) => `Value must be between ${min} and ${max} characters long.`
};

/**
 * @typedef {Object} ValidatorResult
 * @property {string} name
 * @property {boolean} valid
 * @property {string} error
 */

class Validator {

	/**
	 * @return ValidationResult
	 */
	static validateAll = (value, validators) => {
		const errors = [];
		let valid = true;
		validators.every((validator) => {
			const validationResult = validator.validate(value);
			if (!validationResult.valid) {
				valid = false;
				errors.push({
					validator: validationResult.name,
					msg: validationResult.error
				});
			}
		});

		return { valid, errors };
	};

	/**
	 * @param {string} type Validator type
	 * @param {string} name Validator name
	 * @param {Object} params Validator params
	 * @param {string|Function} message
	 */
	constructor(type, name = '', params = {}, message = null) {
		if (!validations.hasOwnProperty(type)) {
			throw new Error(`Unknown validator type [${type}]`);
		}
		this.validationMethod = validations[type];
		this.name = name;
		this.params = params;
		if (message !== null) {
			this.message = message;
		}
		else if (validationMessages.hasOwnProperty(type)) {
			this.message = validationMessages[type];
		}
		else {
			throw new Error(`Unknown validator message for type [${type}]`);
		}
	}

	/**
	 * @param {string} value
	 * @return {ValidatorResult}
	 */
	validate = (value) => {
		const isValid = this.validationMethod.call(null, value, this.params);
		if (isValid) {
			return {
				name: this.name,
				valid: true,
				error: null
			};
		}

		let message = '';
		if (this.message !== null) {
			if (this.message instanceof Function) {
				message = this.message.call(null, { value, ...this.params });
			}
			else {
				message = this.message;
			}
		}

		return {
			name: this.name,
			valid: false,
			error: message
		};
	};
}

export default Validator;


// WEBPACK FOOTER //
// ./components/form/Validators.js